import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { VisibilityLabels } from '@racemap/utilities/consts/events';
import { formatHumanTimeDurationTwoUnits } from '@racemap/utilities/formattings/times';
import { compareNumber } from '@racemap/utilities/functions/compareNumber';
import {
  currentEventTimes,
  isArchivedEvent,
  isDraftOrUnlistedOrArchivedOrNotActivated,
  isNotActivatedEvent,
} from '@racemap/utilities/functions/event';
import { isDefined } from '@racemap/utilities/functions/validation';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { Alert, Button, Flex, Popconfirm, Space } from 'antd';
import classNames from 'classnames';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import type React from 'react';
import { type NavigateFunction, useNavigate } from 'react-router';
import { halfColor, halfTint } from './BasicComponents/DateRangePicker/halfColors';
import { EventLogo } from './BasicComponents/EventLogo';
import { IconInvisible, IconTrash, IconVisible } from './BasicComponents/Icons';
import { EventLink } from './BasicComponents/Links/EventLink';
import { LocalizedTimestampsPopover } from './BasicComponents/LocalizedTimestamps';
import { DragHandle, SortableTable } from './BasicComponents/SortableTable';
import type { ColumnsType } from './BasicComponents/SortableTable/SortableTable';
import { EventInfoBadges } from './EventInfoBadges';

type Props = {
  events: Immutable<Array<RacemapEvent>>;
  eventClassName?: string;
  onEventRemove?: (event: Immutable<RacemapEvent>) => void;
  onSwapEvents?: (e1: Immutable<RacemapEvent>, e2: Immutable<RacemapEvent>) => void;
  onVisibilityToggle: (event: Immutable<RacemapEvent>) => void;
};

export const EventList: React.FC<Props> = ({
  eventClassName,
  onEventRemove,
  onSwapEvents,
  onVisibilityToggle,
  events,
}) => {
  const navigate = useNavigate();
  const sortedEvents = [...events].sort((eA, eB) => compareNumber(eA.index, eB.index));

  const hasNoEvents = events.length === 0;
  const allEventsHidden =
    !hasNoEvents && events.every((e) => isDraftOrUnlistedOrArchivedOrNotActivated(e));
  const overall = getOverallBounds(sortedEvents);

  const columns: ColumnsType<Immutable<RacemapEvent>> = [
    {
      dataIndex: 'id',
      key: 'logo',
      className: 'logo-cell',
      width: 100,
      render: (_id: string, event) => (
        // The handle's own padding already sets it off the cell edge; this matches that on its
        // other side, so the logo is not crowded against it.
        <Flex align="center" gap={8}>
          <DragHandle />
          <EventLogo event={event} size={45} />
        </Flex>
      ),
    },
    {
      title: eventClassName || 'Name',
      dataIndex: 'name',
      key: 'name',
      className: 'name-cell',
      render: (name: string, event) => renderEventNameCell(name, event.id),
    },
    {
      title: 'Start',
      dataIndex: 'startTime',
      key: 'startTime',
      className: 'time-cell',
      width: 105,
      render: (startTime: string) => renderDatetimeCell(startTime),
    },
    {
      // No column name: the header carries the group's whole span, which each row reads against.
      title: overall.span > 0 ? <OverallSpan>Δ {formatSpan(overall.span)}</OverallSpan> : null,
      dataIndex: 'id',
      key: 'duration',
      className: 'duration-cell',
      render: (_id, event) => renderDurationCell(event),
      width: 80,
    },
    {
      title: 'End',
      dataIndex: 'endTime',
      key: 'endTime',
      className: 'time-cell',
      width: 105,
      render: (endTime: string) => renderDatetimeCell(endTime),
    },
    {
      title: 'State',
      dataIndex: 'id',
      key: 'event_state',
      render: (_id, event) => renderBadgeCellVisibility(event),
      width: 155,
    },
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'maintenance',
      className: 'maintenance-cell',
      render: (_id, event) => renderMaintenceCell({ event, onEventRemove, onVisibilityToggle }),
      width: 125,
    },
  ];

  return (
    <Flex gap={7} vertical>
      <Table
        dataSource={sortedEvents}
        pagination={false}
        size="medium"
        // Every column but the name is fixed, so a fixed layout keeps the whole table in view.
        tableLayout="fixed"
        withHandleColumn={false}
        rowClassName={(record) =>
          classNames('event-row', {
            'disabled-row': isNotActivatedEvent(record) || isArchivedEvent(record),
          })
        }
        onRow={(record) => getRowProps(record, overall, navigate)}
        columns={columns}
        onOrderChange={(_data, entry1, entry2) => onSwapEvents?.(entry1, entry2)}
      />
      {hasNoEvents && (
        <Alert
          title="No stages added yet. Add a stage to show events in the player of the group."
          type="warning"
          showIcon
        />
      )}
      {allEventsHidden && (
        <Alert
          title="All events are hidden. You will not see any events in the player of the group."
          type="error"
          showIcon
        />
      )}
    </Flex>
  );
};

const EventName = styled.span`
  // Block, so it takes the cell's width: the cell caps its own at zero to stay flexible, and an
  // inline-block would size itself against that and spill into the Start column.
  display: block;
  // Two lines like the dates beside it, so a one-line name still starts on their first line and a
  // long one wraps into the space already reserved instead of widening the column.
  min-height: 2lh;
  overflow-wrap: anywhere;
`;

const Duration = styled.span`
  color: ${RacemapColors.DarkGray};
  display: inline-block;
  white-space: nowrap;

  // The dates beside it run to two lines. A blank second line here gives all three the same
  // height, so centring each cell still lines their first lines up.
  &::after {
    content: '\\a0';
    display: block;
  }
`;

const OverallSpan = styled.span`
  color: ${RacemapColors.DarkGray};
  font-weight: 400;
  white-space: nowrap;
`;

// Percentage background positions align the same point of image and box, so these two clear the
// bar's start and end — the glow enters at the event's start and leaves at its end, whatever the
// bar's width. It holds off the right edge afterwards rather than snapping back. The second value
// pins the bar's own gradient, which is the layer underneath.
const sweep = keyframes`
  from { background-position-x: -50%, 0%; }
  to { background-position-x: 150%, 0%; }
`;

const Table = styled(SortableTable<Immutable<RacemapEvent>>)`
  border: 1px solid ${RacemapColors.LightGray};
  border-bottom: unset;
  display: block;
  overflow-x: auto;
  // Left alone, overflow-y resolves from 'visible' to 'auto' because the other axis is not
  // visible, and a one-pixel overflow then produced a scrollbar. Height stays content-driven.
  overflow-y: hidden;

  tr.event-row {
    position: relative;
    cursor: pointer;
  }

  // Handle, logo and name read as one leading unit; the default cell padding doubles every gap.
  .logo-cell {
    padding-right: 0;
  }

  // The only flexible column. Together these two claim exactly the width the fixed columns leave:
  // 100% takes the leftover, and max-width stops a long name from taking more than that — it wraps
  // instead. They also leave nothing for the phantom column the row's Gantt bar would be given.
  .name-cell {
    width: 100%;
    max-width: 0;
    padding-left: 8px;
  }

  // Each line of the two-line label stays whole; the column is sized for the longer one.
  .time-cell {
    white-space: nowrap;
  }

  // Where this event sits in the group's whole span. Down the column, a small Gantt chart.
  tr.event-row::after {
    content: '';
    position: absolute;
    left: var(--duration-offset, 0);
    bottom: 0;
    height: 1px;
    // A zero-length event would otherwise be invisible.
    min-width: 1px;
    width: var(--duration-share, 0);
    // The range encoding the date pickers use: blue where the event begins, violet where it ends.
    background-image: linear-gradient(90deg, ${halfColor.start}, ${halfColor.end});
  }

  // Hovering a row runs the glow once from the event's start to its end, over that gradient.
  tr.event-row:hover::after {
    height: 2px;
    background-image:
      linear-gradient(90deg, transparent, ${halfTint.start}, ${halfTint.end}, transparent),
      linear-gradient(90deg, ${halfColor.start}, ${halfColor.end});
    background-repeat: no-repeat;
    // A third of the bar wide, so the glow reads as a highlight travelling along it.
    background-size:
      33% 100%,
      100% 100%;
    animation: ${sweep} 600ms ease-out forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    tr.event-row:hover::after {
      animation: none;
    }
  }

  .ant-table-tbody > tr.disabled-row {
    td > * {
    opacity: 0.5;
    }
    maintenance-cell > * {
      opacity: 1;
    }
  }

  .ant-table-tbody > tr.is-dragging > td {
    border-top: 1px solid ${RacemapColors.LightGray};
  }
`;

/** Earliest child start to latest child end — the window every row's bar is placed inside. */
const getOverallBounds = (
  events: Immutable<Array<RacemapEvent>>,
): { start: number; span: number } => {
  const times = events.map((event) => currentEventTimes(event));
  const starts = times.map(([start]) => start).filter(isDefined);
  const ends = times.map(([, end]) => end).filter(isDefined);
  if (starts.length === 0 || ends.length === 0) return { start: 0, span: 0 };

  const start = Math.min(...starts);
  return { start, span: Math.max(...ends) - start };
};

const getDuration = (event: Immutable<RacemapEvent>): number | null => {
  const [start, end] = currentEventTimes(event);
  if (start == null || end == null) return null;
  return end - start;
};

const formatSpan = (millis: number) => formatHumanTimeDurationTwoUnits(millis);

const MIDDLE_BUTTON = 1;

// Element, not HTMLElement: the badge icons are SVG, and a click on one is still the row's.
const isOwnClick = (target: EventTarget | null) =>
  target instanceof Element && target.closest('button, a, .ant-popconfirm, .ant-popover') == null;

const getRowProps = (
  record: Immutable<RacemapEvent>,
  overall: { start: number; span: number },
  navigate: NavigateFunction,
): React.HTMLAttributes<HTMLTableRowElement> => {
  if (record == null) return {};

  const [start, end] = currentEventTimes(record);
  const placeable = overall.span > 0 && start != null && end != null;
  const share = placeable ? ((end - start) / overall.span) * 100 : 0;
  const offset = placeable ? ((start - overall.start) / overall.span) * 100 : 0;

  const link = `/admin/events/${record.id}`;

  const props: React.HTMLAttributes<HTMLTableRowElement> = {
    style: {
      '--duration-share': `${share}%`,
      '--duration-offset': `${offset}%`,
    } as React.CSSProperties,
    onClick: (e) => {
      if (!isOwnClick(e.target)) return;
      if (e.ctrlKey || e.metaKey) {
        window.open(link, '_blank', 'noopener');
        return;
      }
      navigate(link);
    },
    // Middle click opens a new tab anywhere on the row, matching what the name link already does.
    onAuxClick: (e) => {
      if (e.button !== MIDDLE_BUTTON || !isOwnClick(e.target)) return;
      window.open(link, '_blank', 'noopener');
    },
    // Without this the middle button starts the browser's autoscroll instead.
    onMouseDown: (e) => {
      if (e.button === MIDDLE_BUTTON && isOwnClick(e.target)) e.preventDefault();
    },
  };

  if (isArchivedEvent(record))
    return {
      ...props,
      title: `The Event is ${
        VisibilityLabels[record.visibility]
      } State. So it is not visibel for the spectators in the group.`,
    };
  if (isNotActivatedEvent(record))
    return {
      ...props,
      title: 'The Event is still trial. So it is not activated yet.',
    };

  return props;
};

const renderMaintenceCell = ({
  event,
  onEventRemove,
  onVisibilityToggle,
}: {
  event: Immutable<RacemapEvent>;
  onEventRemove: Props['onEventRemove'];
  onVisibilityToggle: Props['onVisibilityToggle'];
}) => {
  const isUnlistedEvent = isDraftOrUnlistedOrArchivedOrNotActivated(event);

  return (
    <Space orientation="vertical" style={{ width: '100%' }}>
      <FullWidthButton
        title={isUnlistedEvent ? 'Click to show event' : 'Click to hide event'}
        icon={isUnlistedEvent ? <IconVisible /> : <IconInvisible />}
        disabled={isNotActivatedEvent(event) || isArchivedEvent(event)}
        onClick={() => onVisibilityToggle(event)}
      >
        {isUnlistedEvent ? 'Show' : 'Hide'}
      </FullWidthButton>
      {onEventRemove != null && (
        <Popconfirm onConfirm={() => onEventRemove(event)} title="Are you sure?">
          <FullWidthButton icon={<IconTrash />} danger>
            Remove
          </FullWidthButton>
        </Popconfirm>
      )}
    </Space>
  );
};

const renderEventNameCell = (name: string, id?: string) => {
  if (id == null) return <EventName>{name}</EventName>;
  return (
    <EventName>
      {/* A link renders as an antd button, which is nowrap and one line tall. A long name has to
          wrap here, and wrapped lines read left-aligned. */}
      <EventLink
        eventId={id}
        eventName={name}
        style={{ whiteSpace: 'normal', height: 'auto', textAlign: 'left' }}
      />
    </EventName>
  );
};

const renderDatetimeCell = (datetime: string) => {
  const parsed = DateTime.fromISO(datetime);

  return (
    <LocalizedTimestampsPopover
      showUTC
      timestamps={[{ timestamp: parsed }]}
      // Date and time on separate lines: the column then needs half the width and still wraps
      // nowhere.
      label={
        <>
          {parsed.toLocaleString(DateTime.DATE_MED)}
          <br />
          {parsed.toLocaleString(DateTime.TIME_SIMPLE)}
        </>
      }
    />
  );
};

const renderDurationCell = (event: Immutable<RacemapEvent>) => {
  const duration = getDuration(event);
  if (duration == null) return null;

  return <Duration title="Duration of the event">Δ {formatSpan(duration)}</Duration>;
};

const renderBadgeCellVisibility = (event?: Immutable<RacemapEvent>) => (
  <EventInfoBadges event={event} wrap />
);

const FullWidthButton = styled(Button)`
  width: 100%;
`;
